import { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, CheckCircle, XCircle, Clock, Link, ExternalLink, Gavel, Download, AlertTriangle, Mail } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { AuthContext } from '@/App';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DECISION_MAKERS = [
  'HLRA Committee Meeting',
  'HLRA AGM',
  'Action Outcome',
  'Email Consensus',
  'Chair Decision',
  'Other'
];

const DECISION_STATUSES = ['Pending', 'Approved', 'Rejected'];

// Helper function to format user display with organization tag
const formatUserDisplay = (user) => {
  if (!user) return 'Unknown';
  const name = user.name || 'Unknown';
  
  // Use display_group if available
  if (user.display_group) {
    return `${name} (${user.display_group})`;
  }
  
  if (user.role === 'external_org' && user.organization) {
    return `${name} (${user.organization})`;
  } else if (user.organization && user.organization !== 'HLRA') {
    return `${name} (${user.organization})`;
  } else {
    return name;
  }
};

const getOrgBadgeColor = (user) => {
  if (!user) return '';
  if (user.display_group_color) {
    return '';
  }
  if (user.role === 'external_org' || (user.organization && user.organization !== 'HLRA')) {
    return 'text-purple-600';
  }
  return 'text-blue-600';
};

export default function Decisions() {
  const { user: currentUser } = useContext(AuthContext);
  const [decisions, setDecisions] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateActionDialog, setShowCreateActionDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLinkedActions, setDeleteLinkedActions] = useState(false);
  const [linkedActionsForDelete, setLinkedActionsForDelete] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [selectedActionItem, setSelectedActionItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState(null);

  // Role checks
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isAdmin = currentUser?.role === 'admin' || isSuperAdmin;
  const isCommittee = currentUser?.role === 'committee';
  const isExternalOrg = currentUser?.role === 'external_org';
  
  // Can create/edit decisions: Admin, SuperAdmin, Committee (NOT external_org)
  const canCreateDecisions = isAdmin || isCommittee;
  // Can export PDF: All authenticated users (including external)
  const canExportPDF = true;

  const [newDecision, setNewDecision] = useState({
    date: new Date().toISOString().split('T')[0],
    description: '',
    decision_maker: '',
    meeting_id: '',
    action_id: '',
    rationale: '',
    impact: '',
    status: 'Pending',
    action_items: []
  });

  const [newActionItem, setNewActionItem] = useState('');
  
  const [createActionForm, setCreateActionForm] = useState({
    owner_id: '',
    due_date: '',
    priority: 'Medium',
    category: ''
  });

  useEffect(() => {
    fetchData();
  }, [filterStatus]);

  const fetchData = async () => {
    try {
      const [decisionsRes, meetingsRes, actionsRes, usersRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/decisions${filterStatus ? `?status=${filterStatus}` : ''}`),
        axios.get(`${API}/meetings`),
        axios.get(`${API}/actions`),
        axios.get(`${API}/users/with-groups`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/decisions/stats/summary`)
      ]);
      setDecisions(decisionsRes.data);
      setMeetings(meetingsRes.data);
      setActions(actionsRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDecision = async () => {
    if (!newDecision.description.trim()) {
      toast.error('Description is required');
      return;
    }
    if (!newDecision.decision_maker) {
      toast.error('Decision maker is required');
      return;
    }
    if (newDecision.description.length > 500) {
      toast.error('Description must be 500 characters or less');
      return;
    }

    try {
      const payload = {
        ...newDecision,
        meeting_id: newDecision.meeting_id || null,
        action_id: newDecision.action_id || null
      };
      await axios.post(`${API}/decisions`, payload);
      toast.success('Decision created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create decision');
    }
  };

  const handleUpdateDecision = async () => {
    if (!selectedDecision) return;

    try {
      await axios.put(`${API}/decisions/${selectedDecision.id}`, {
        date: selectedDecision.date,
        description: selectedDecision.description,
        decision_maker: selectedDecision.decision_maker,
        meeting_id: selectedDecision.meeting_id || null,
        action_id: selectedDecision.action_id || null,
        rationale: selectedDecision.rationale,
        impact: selectedDecision.impact,
        status: selectedDecision.status,
        action_items: selectedDecision.action_items
      });
      toast.success('Decision updated successfully');
      setShowEditDialog(false);
      setSelectedDecision(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update decision');
    }
  };

  const handleOpenDeleteDialog = async (decision) => {
    if (!isSuperAdmin) {
      toast.error('Only Super Admins can delete decisions');
      return;
    }
    
    setSelectedDecision(decision);
    setDeleteLinkedActions(false);
    
    // Check for linked actions
    try {
      const response = await axios.get(`${API}/decisions/${decision.id}/linked-actions`);
      setLinkedActionsForDelete(response.data.linked_actions || []);
    } catch (error) {
      setLinkedActionsForDelete([]);
    }
    
    setShowDeleteDialog(true);
  };

  const handleDeleteDecision = async () => {
    if (!selectedDecision) return;

    try {
      const params = new URLSearchParams();
      if (deleteLinkedActions) {
        params.append('delete_linked_actions', 'true');
      }
      
      const response = await axios.delete(`${API}/decisions/${selectedDecision.id}?${params.toString()}`);
      
      let message = 'Decision deleted successfully';
      if (response.data.deleted_actions && response.data.deleted_actions.length > 0) {
        message += ` (including ${response.data.deleted_actions.length} linked action(s))`;
      }
      
      toast.success(message);
      setShowDeleteDialog(false);
      setSelectedDecision(null);
      setLinkedActionsForDelete([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete decision');
    }
  };

  const handleExportPDF = async () => {
    try {
      toast.info('Generating PDF...');
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      
      const response = await axios.get(`${API}/decisions/export/pdf?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `HLRA_Decisions_Log_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.error('Failed to export PDF');
    }
  };

  const handleEmailPDF = async () => {
    try {
      toast.info('Sending report to your email...');
      
      const response = await axios.post(`${API}/reports/email-to-me`, {
        report_type: 'decisions',
        filters: { status: filterStatus || null }
      });
      
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleAddActionItem = () => {
    if (!newActionItem.trim()) return;
    setNewDecision({
      ...newDecision,
      action_items: [...newDecision.action_items, { description: newActionItem, action_id: null }]
    });
    setNewActionItem('');
  };

  const handleRemoveActionItem = (index) => {
    const items = [...newDecision.action_items];
    items.splice(index, 1);
    setNewDecision({ ...newDecision, action_items: items });
  };

  const handleAddActionItemToEdit = () => {
    if (!newActionItem.trim()) return;
    setSelectedDecision({
      ...selectedDecision,
      action_items: [...(selectedDecision.action_items || []), { description: newActionItem, action_id: null }]
    });
    setNewActionItem('');
  };

  const handleRemoveActionItemFromEdit = (index) => {
    const items = [...selectedDecision.action_items];
    items.splice(index, 1);
    setSelectedDecision({ ...selectedDecision, action_items: items });
  };

  const handleCreateActionFromItem = async () => {
    if (!selectedDecision || selectedActionItem === null) return;

    try {
      await axios.post(`${API}/decisions/${selectedDecision.id}/create-action`, {
        action_item_index: selectedActionItem,
        owner_id: createActionForm.owner_id,
        due_date: createActionForm.due_date || null,
        priority: createActionForm.priority,
        category: createActionForm.category || 'General'
      });
      toast.success('Action created successfully');
      setShowCreateActionDialog(false);
      setSelectedActionItem(null);
      setCreateActionForm({ owner_id: '', due_date: '', priority: 'Medium', category: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create action');
    }
  };

  const resetForm = () => {
    setNewDecision({
      date: new Date().toISOString().split('T')[0],
      description: '',
      decision_maker: '',
      meeting_id: '',
      action_id: '',
      rationale: '',
      impact: '',
      status: 'Pending',
      action_items: []
    });
    setNewActionItem('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Approved':
        return <Badge className="bg-green-100 text-green-800 hover:bg-green-200"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'Rejected':
        return <Badge className="bg-red-100 text-red-800 hover:bg-red-200"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const getMeetingName = (meetingId) => {
    const meeting = meetings.find(m => m.id === meetingId);
    return meeting ? `${meeting.title} (${new Date(meeting.date).toLocaleDateString('en-GB')})` : null;
  };

  const getActionReference = (actionId) => {
    const action = actions.find(a => a.id === actionId);
    return action ? action.reference : null;
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? formatUserDisplay(user) : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="decisions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Gavel className="w-8 h-8 text-indigo-600" />
            Decisions
          </h1>
          <p className="text-gray-600 mt-1">
            {isExternalOrg 
              ? "View formal HLRA decisions" 
              : "Track and manage formal HLRA decisions"}
          </p>
        </div>
        <div className="flex gap-2">
          {canExportPDF && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" data-testid="export-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPDF}>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEmailPDF}>
                  <Mail className="w-4 h-4 mr-2" />
                  Email to me
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canCreateDecisions && (
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-decision-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Record Decision
                </Button>
              </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Record New Decision</DialogTitle>
                <DialogDescription>Capture a formal decision made by HLRA</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newDecision.date}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setNewDecision({ ...newDecision, date: e.target.value })}
                    data-testid="decision-date"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={newDecision.status} onValueChange={(v) => setNewDecision({ ...newDecision, status: v })}>
                    <SelectTrigger data-testid="decision-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DECISION_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description * <span className="text-gray-400 text-xs">({newDecision.description.length}/500)</span></Label>
                <Textarea
                  placeholder="Clearly describe the decision..."
                  value={newDecision.description}
                  maxLength={500}
                  onChange={(e) => setNewDecision({ ...newDecision, description: e.target.value })}
                  className="min-h-[100px]"
                  data-testid="decision-description"
                />
              </div>

              <div>
                <Label>Decision Maker *</Label>
                <Select value={newDecision.decision_maker} onValueChange={(v) => setNewDecision({ ...newDecision, decision_maker: v })}>
                  <SelectTrigger data-testid="decision-maker">
                    <SelectValue placeholder="Select where/how decision was made" />
                  </SelectTrigger>
                  <SelectContent>
                    {DECISION_MAKERS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Linked Meeting (optional)</Label>
                  <Select value={newDecision.meeting_id || 'none'} onValueChange={(v) => setNewDecision({ ...newDecision, meeting_id: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select meeting" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {meetings.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.title} ({new Date(m.date).toLocaleDateString('en-GB')})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Linked Action (optional)</Label>
                  <Select value={newDecision.action_id || 'none'} onValueChange={(v) => setNewDecision({ ...newDecision, action_id: v === 'none' ? '' : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select action" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {actions.slice(0, 50).map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.reference}: {a.title.substring(0, 40)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Rationale</Label>
                <Textarea
                  placeholder="The reasoning or data supporting this decision..."
                  value={newDecision.rationale}
                  onChange={(e) => setNewDecision({ ...newDecision, rationale: e.target.value })}
                  className="min-h-[80px]"
                  data-testid="decision-rationale"
                />
              </div>

              <div>
                <Label>Impact</Label>
                <Textarea
                  placeholder="How this decision impacts..."
                  value={newDecision.impact}
                  onChange={(e) => setNewDecision({ ...newDecision, impact: e.target.value })}
                  className="min-h-[80px]"
                  data-testid="decision-impact"
                />
              </div>

              <div>
                <Label>Action Items</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Add an action item..."
                    value={newActionItem}
                    onChange={(e) => setNewActionItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddActionItem())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddActionItem}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {newDecision.action_items.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {newDecision.action_items.map((item, idx) => (
                      <li key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                        <span>{item.description}</span>
                        <Button variant="ghost" size="sm" onClick={() => handleRemoveActionItem(idx)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button onClick={handleCreateDecision} className="bg-indigo-600 hover:bg-indigo-700">
                Record Decision
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-indigo-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-indigo-600">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Decisions</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-yellow-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
              <div className="text-sm text-gray-600">Pending</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-green-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
              <div className="text-sm text-gray-600">Approved</div>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{stats.rejected}</div>
              <div className="text-sm text-gray-600">Rejected</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="w-48">
              <Label className="text-xs text-gray-500">Filter by Status</Label>
              <Select value={filterStatus || 'all'} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {DECISION_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Decisions List */}
      <div className="space-y-4">
        {decisions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Gavel className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900">No decisions recorded</h3>
              <p className="text-gray-500 mt-1">Click "Record Decision" to capture your first decision</p>
            </CardContent>
          </Card>
        ) : (
          decisions.map((decision) => (
            <Card key={decision.id} className="hover:shadow-md transition-shadow" data-testid={`decision-${decision.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-indigo-600">{decision.reference}</span>
                    {getStatusBadge(decision.status)}
                  </div>
                  {isSuperAdmin && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedDecision({ ...decision });
                          setShowEditDialog(true);
                        }}
                        title="Edit decision"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleOpenDeleteDialog(decision)}
                        title="Delete decision"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
                <CardDescription className="mt-1">
                  <span className="text-gray-600">
                    {new Date(decision.date).toLocaleDateString('en-GB', { 
                      weekday: 'short', 
                      day: 'numeric', 
                      month: 'short', 
                      year: 'numeric' 
                    })}
                  </span>
                  <span className="mx-2">•</span>
                  <span className="text-gray-600">{decision.decision_maker}</span>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-900">{decision.description}</p>
                
                {(decision.meeting_id || decision.action_id) && (
                  <div className="flex flex-wrap gap-2">
                    {decision.meeting_id && getMeetingName(decision.meeting_id) && (
                      <Badge variant="outline" className="text-blue-600 border-blue-200">
                        <Link className="w-3 h-3 mr-1" />
                        Meeting: {getMeetingName(decision.meeting_id)}
                      </Badge>
                    )}
                    {decision.action_id && getActionReference(decision.action_id) && (
                      <RouterLink to={`/actions/${decision.action_id}`}>
                        <Badge variant="outline" className="text-green-600 border-green-200 cursor-pointer hover:bg-green-50">
                          <Link className="w-3 h-3 mr-1" />
                          Action: {getActionReference(decision.action_id)}
                        </Badge>
                      </RouterLink>
                    )}
                  </div>
                )}

                {decision.rationale && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-800 mb-1">Rationale</h4>
                    <p className="text-sm text-blue-700">{decision.rationale}</p>
                  </div>
                )}

                {decision.impact && (
                  <div className="bg-amber-50 p-3 rounded-lg">
                    <h4 className="text-sm font-semibold text-amber-800 mb-1">Impact</h4>
                    <p className="text-sm text-amber-700">{decision.impact}</p>
                  </div>
                )}

                {decision.action_items && decision.action_items.length > 0 && (
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-800 mb-2">Action Items</h4>
                    <ul className="space-y-2">
                      {decision.action_items.map((item, idx) => (
                        <li key={idx} className="flex items-center justify-between text-sm">
                          <span className="flex items-center gap-2">
                            <span className="w-5 h-5 bg-gray-200 rounded-full flex items-center justify-center text-xs font-medium">
                              {idx + 1}
                            </span>
                            {item.description}
                          </span>
                          {item.action_id ? (
                            <RouterLink to={`/actions/${item.action_id}`}>
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer">
                                <ExternalLink className="w-3 h-3 mr-1" />
                                {item.action_reference || 'View Action'}
                              </Badge>
                            </RouterLink>
                          ) : canCreateDecisions ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => {
                                setSelectedDecision(decision);
                                setSelectedActionItem(idx);
                                setShowCreateActionDialog(true);
                              }}
                            >
                              <Plus className="w-3 h-3 mr-1" />
                              Create Action
                            </Button>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No action linked</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="text-xs text-gray-400 pt-2 border-t">
                  Created by {getUserName(decision.created_by)} on{' '}
                  {new Date(decision.created_at).toLocaleDateString('en-GB')}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Decision {selectedDecision?.reference}</DialogTitle>
          </DialogHeader>
          {selectedDecision && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={selectedDecision.date}
                    max={new Date().toISOString().split('T')[0]}
                    onChange={(e) => setSelectedDecision({ ...selectedDecision, date: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={selectedDecision.status} onValueChange={(v) => setSelectedDecision({ ...selectedDecision, status: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DECISION_STATUSES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Description ({selectedDecision.description.length}/500)</Label>
                <Textarea
                  value={selectedDecision.description}
                  maxLength={500}
                  onChange={(e) => setSelectedDecision({ ...selectedDecision, description: e.target.value })}
                  className="min-h-[100px]"
                />
              </div>

              <div>
                <Label>Decision Maker</Label>
                <Select value={selectedDecision.decision_maker} onValueChange={(v) => setSelectedDecision({ ...selectedDecision, decision_maker: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DECISION_MAKERS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Linked Meeting</Label>
                  <Select value={selectedDecision.meeting_id || 'none'} onValueChange={(v) => setSelectedDecision({ ...selectedDecision, meeting_id: v === 'none' ? null : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {meetings.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.title} ({new Date(m.date).toLocaleDateString('en-GB')})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Linked Action</Label>
                  <Select value={selectedDecision.action_id || 'none'} onValueChange={(v) => setSelectedDecision({ ...selectedDecision, action_id: v === 'none' ? null : v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {actions.slice(0, 50).map(a => (
                        <SelectItem key={a.id} value={a.id}>
                          {a.reference}: {a.title.substring(0, 40)}...
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Rationale</Label>
                <Textarea
                  value={selectedDecision.rationale || ''}
                  onChange={(e) => setSelectedDecision({ ...selectedDecision, rationale: e.target.value })}
                  className="min-h-[80px]"
                />
              </div>

              <div>
                <Label>Impact</Label>
                <Textarea
                  value={selectedDecision.impact || ''}
                  onChange={(e) => setSelectedDecision({ ...selectedDecision, impact: e.target.value })}
                  className="min-h-[80px]"
                />
              </div>

              <div>
                <Label>Action Items</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Add an action item..."
                    value={newActionItem}
                    onChange={(e) => setNewActionItem(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddActionItemToEdit())}
                  />
                  <Button type="button" variant="outline" onClick={handleAddActionItemToEdit}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {selectedDecision.action_items && selectedDecision.action_items.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {selectedDecision.action_items.map((item, idx) => (
                      <li key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                        <span className="flex items-center gap-2">
                          {item.description}
                          {item.action_id && (
                            <Badge className="bg-green-100 text-green-700 text-xs">
                              {item.action_reference || 'Linked'}
                            </Badge>
                          )}
                        </span>
                        {!item.action_id && (
                          <Button variant="ghost" size="sm" onClick={() => handleRemoveActionItemFromEdit(idx)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button onClick={handleUpdateDecision} className="bg-indigo-600 hover:bg-indigo-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Action from Item Dialog */}
      <Dialog open={showCreateActionDialog} onOpenChange={setShowCreateActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Action from Decision Item</DialogTitle>
            <DialogDescription>
              {selectedDecision && selectedActionItem !== null && selectedDecision.action_items[selectedActionItem] && (
                <span className="block mt-2 p-2 bg-gray-100 rounded text-gray-800">
                  "{selectedDecision.action_items[selectedActionItem].description}"
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Assign To *</Label>
              <Select value={createActionForm.owner_id} onValueChange={(v) => setCreateActionForm({ ...createActionForm, owner_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select assignee" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <span className="flex items-center gap-2">
                        {user.name}
                        <span className={`text-xs ${getOrgBadgeColor(user)}`}>
                          ({user.role === 'external_org' || (user.organization && user.organization !== 'HLRA') ? user.organization : 'HLRA'})
                        </span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input
                type="date"
                value={createActionForm.due_date}
                onChange={(e) => setCreateActionForm({ ...createActionForm, due_date: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Priority</Label>
                <Select value={createActionForm.priority} onValueChange={(v) => setCreateActionForm({ ...createActionForm, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={createActionForm.category} onValueChange={(v) => setCreateActionForm({ ...createActionForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleCreateActionFromItem} 
              className="bg-green-600 hover:bg-green-700"
              disabled={!createActionForm.owner_id}
            >
              Create Action
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Decision
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete decision <strong>{selectedDecision?.reference}</strong>?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          
          {linkedActionsForDelete.length > 0 && (
            <div className="py-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-800 mb-2">
                  ⚠️ This decision has {linkedActionsForDelete.length} linked action(s):
                </h4>
                <ul className="space-y-1 mb-4">
                  {linkedActionsForDelete.map(action => (
                    <li key={action.id} className="text-sm text-amber-700 flex items-center gap-2">
                      <span className="font-mono bg-amber-100 px-1 rounded">{action.reference}</span>
                      <span>{action.title}</span>
                      <Badge variant="outline" className="text-xs">{action.status}</Badge>
                    </li>
                  ))}
                </ul>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="deleteLinkedActions"
                    checked={deleteLinkedActions}
                    onChange={(e) => setDeleteLinkedActions(e.target.checked)}
                    className="h-4 w-4 text-red-600 rounded border-gray-300"
                  />
                  <Label htmlFor="deleteLinkedActions" className="text-sm text-amber-800 cursor-pointer">
                    Also delete these linked actions
                  </Label>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteDecision}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete {deleteLinkedActions && linkedActionsForDelete.length > 0 ? 'All' : 'Decision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
